// Capture des douchettes HID — portage TypeScript de useHiddenScanInput /
// useScanner de la V2, comportement inchangé.
//
// Un <input> hors écran reste focalisé pour que les frappes émulées par la
// douchette atterrissent quelque part. Deux modes de livraison :
//   • HID clavier — un keydown par caractère, Entrée termine le scan ;
//   • IME / soft-wedge — tous les caractères en un seul événement `input`,
//     sans Entrée : on déclenche après `imeTimeout` ms.
//
// `actif=false` retire complètement l'input : à utiliser sur les écrans qui
// ont leurs propres champs de saisie, sinon le scanner leur vole le focus.

import { useEffect, useRef } from 'react'

export interface OptionsScan {
  actif?: boolean
  imeTimeout?: number
  minLength?: number
}

export function useScanCache(
  onScan: (valeur: string) => void,
  { actif = true, imeTimeout = 200, minLength = 1 }: OptionsScan = {},
): void {
  const ref = useRef(onScan)
  useEffect(() => { ref.current = onScan }, [onScan])

  useEffect(() => {
    if (!actif || typeof document === 'undefined') return

    let vivant = true
    let attente: number | undefined

    const input = document.createElement('input')
    input.type = 'text'
    input.setAttribute('inputmode', 'none')
    input.setAttribute('autocomplete', 'off')
    input.setAttribute('autocorrect', 'off')
    input.setAttribute('autocapitalize', 'off')
    input.setAttribute('spellcheck', 'false')
    input.setAttribute('aria-hidden', 'true')
    input.tabIndex = -1
    input.style.cssText =
      'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;'
    document.body.appendChild(input)

    const tirer = () => {
      window.clearTimeout(attente)
      const val = input.value.trim()
      input.value = ''
      if (vivant && val.length >= minLength) ref.current(val)
    }

    const surTouche = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (e.key === 'Enter') { e.preventDefault(); tirer() }
    }
    const surSaisie = () => {
      window.clearTimeout(attente)
      attente = window.setTimeout(tirer, imeTimeout)
    }
    // Reprendre le focus, mais jamais à un vrai champ ni à un bouton en cours
    // de clic — sinon on casse la saisie manuelle de l'opérateur.
    const surPerte = () => {
      window.setTimeout(() => {
        if (!vivant) return
        const tag = document.activeElement?.tagName
        if (!tag || tag === 'BODY' || tag === 'BUTTON') input.focus({ preventScroll: true })
      }, 50)
    }

    input.addEventListener('keydown', surTouche)
    input.addEventListener('input', surSaisie)
    input.addEventListener('blur', surPerte)
    input.focus({ preventScroll: true })

    return () => {
      vivant = false
      window.clearTimeout(attente)
      input.removeEventListener('keydown', surTouche)
      input.removeEventListener('input', surSaisie)
      input.removeEventListener('blur', surPerte)
      input.remove()
    }
  }, [actif, imeTimeout, minLength])
}

/**
 * Compatibilité avec les appelants V2 : `{ minLength, timeout }` où `timeout`
 * était l'écart entre deux frappes. Il alimente ici le délai de complétion IME.
 * Pour une douchette USB de bureau (qui envoie Entrée) la valeur ne sert pas.
 */
export function useScanner(
  onScan: (valeur: string) => void,
  { minLength = 4, timeout = 80, actif = true }: { minLength?: number; timeout?: number; actif?: boolean } = {},
): void {
  useScanCache(onScan, { imeTimeout: timeout + 40, minLength, actif })
}
